import React, { useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { FormLabel } from "react-bootstrap";
import AsyncSelect from "react-select/async";
import Select from "react-select";
import Skeleton from "react-loading-skeleton";
import { Filter, X } from "react-feather";
import { toast } from "react-toastify";
import { Col, Input, Nav, NavItem, NavLink, TabContent, TabPane, Table, Alert } from "reactstrap";
import { Breadcrumbs, H5, ToolTip } from "../../AbstractElements";
import { useLoader } from "../../_helper/LoaderContext";
import PrivilegeContext from "../../_helper/PrivilegeContext";
import { usePharmacy } from "../../_helper/PharmacyProvider";
import { canShowPatientAccounts } from "../../utils/pharmacyConnection";
import { useCurrency } from "../../_helper/CurrencyContext";
import { getAllPatients } from "../PharmacySales/SalesService";
import { mapPatientsToOptions } from "../PharmacySales/NewSales/utils/patientUtils";
import MyPagination from "../pagination";
import CollectPaymentModal from "./components/CollectPaymentModal";
import InvoicePreviewModal from "./components/InvoicePreviewModal";
import InvoiceStatusBadge from "./components/InvoiceStatusBadge";
import HighlightOptions from "../InvoiceModule/HighlightOptions";
import PatientAccountPaidAmount from "./components/PatientAccountPaidAmount";
import {
	getPatientInvoicesAPI,
	payRemainingSalesAPI,
} from "./PatientAccountsService";
import {
	formatAmount,
	formatDateList,
	getCollectPaymentAppointmentId,
	getPatientAccountPatientId,
	getPatientDisplayName,
	getTodayDateString,
	normalizePatientAccountItem,
	buildPayRemainingSalesPayload,
	savePatientAccountRecord,
} from "./utils/patientAccountUtils";
import "./patient-accounts-style.scss";

const MENU_KEY = "patient_accounts";
const SUB_PERMISSION_KEY = "patient_accounts";
const TAB_STORAGE_KEY = "patient-accounts-tab";

const readSavedTab = () =>
	sessionStorage.getItem(TAB_STORAGE_KEY) === "paid" ? "paid" : "partial_paid";

const resolveNavPatientState = (navState) => {
	const state = navState || {};
	const patientId = state.patientId || "";
	const patientName = state.patientName || "";
	const hasNavPatient = Boolean(patientId && patientName);

	return {
		hasNavPatient,
		patientId,
		patientName,
		activeTab:
			state.activeTab === "paid"
				? "paid"
				: hasNavPatient
					? "partial_paid"
					: readSavedTab(),
		selectedPatientOption: hasNavPatient
			? { value: patientId, label: patientName }
			: null,
		appliedFilters: {
			patientId: hasNavPatient ? patientId : "",
			fromDate: "",
			toDate: "",
			invoiceType: "all",
			search: "",
		},
	};
};

const PatientAccounts = () => {
	const { t } = useTranslation();
	const { setLoading } = useLoader();
	const { privileges } = useContext(PrivilegeContext);
	const { pharmacyPOS } = usePharmacy();
	const { getCurrencySymbol } = useCurrency();
	const history = useNavigate();
	const location = useLocation();
	const navPatientState = resolveNavPatientState(location.state);

	const [activeTab, setActiveTab] = useState(navPatientState.activeTab);
	const [skeletonLoading, setSkeletonLoading] = useState(true);
	const [listData, setListData] = useState([]);
	const [totalItems, setTotalItems] = useState(0);
	const [page, setPage] = useState(1);
	const [size, setSize] = useState(10);
	const [selectPatient, setSelectPatient] = useState(navPatientState.patientId);
	const [selectedPatientOption, setSelectedPatientOption] = useState(
		navPatientState.selectedPatientOption
	);
	const [fromDate, setFromDate] = useState("");
	const [toDate, setToDate] = useState("");
	const [selectType, setSelectType] = useState("all");
	const [searchValue, setSearchValue] = useState("");
	const [appliedFilters, setAppliedFilters] = useState(navPatientState.appliedFilters);

	const today = getTodayDateString();

	const typeOptions = [
		{ value: "all", label: t("All") },
		{ value: "consultation", label: t("Consultation") },
		{ value: "treatment_package", label: t("Treatment Package") },
	];

	const [listView, setListView] = useState(false);
	const [viewView, setViewView] = useState(false);
	const [collectView, setCollectView] = useState(false);

	const [previewModal, setPreviewModal] = useState(false);
	const [previewInvoice, setPreviewInvoice] = useState(null);
	const [collectModal, setCollectModal] = useState(false);
	const [selectedInvoice, setSelectedInvoice] = useState(null);
	const [isSubmittingPayment, setIsSubmittingPayment] = useState(false);

	const [filterTooltipOpen, setFilterTooltipOpen] = useState(false);
	const [clearTooltipOpen, setClearTooltipOpen] = useState(false);
	const [actionTooltips, setActionTooltips] = useState({});

	const toggleActionTooltip = (id) => {
		setActionTooltips((prev) => ({ ...prev, [id]: !prev[id] }));
	};

	const getPermission = useCallback(() => {
		if (privileges?.permissions) {
			const permissionMap = {
				list: setListView,
				view: setViewView,
				collect_payment: setCollectView,
				update: setCollectView,
			};

			privileges.permissions.forEach((element) => {
				if (element.menu_key === MENU_KEY && element.sub_permissions) {
					element.sub_permissions.forEach((child) => {
						if (child.key === SUB_PERMISSION_KEY && child.actions) {
							child.actions.forEach((action) => {
								if (action.key in permissionMap && action.allowed) {
									permissionMap[action.key](true);
								}
							});
						}
					});
				}

				if (element.menu_key === MENU_KEY && element.actions) {
					element.actions.forEach((action) => {
						if (action.key in permissionMap && action.allowed) {
							permissionMap[action.key](true);
						}
					});
				}
			});
		} else {
			setListView(true);
			setViewView(true);
			setCollectView(true);
		}
	}, [privileges]);

	const getList = useCallback(async () => {
		const body = {
			clinic_id: pharmacyPOS?.hospital_id || "",
			pageNo: page,
			pageSize: size,
			status: activeTab === "paid" ? "paid" : "partially_paid",
			patient_id: appliedFilters.patientId,
			from_date: appliedFilters.fromDate,
			to_date: appliedFilters.toDate,
			type: appliedFilters.invoiceType || "all",
			search: appliedFilters.search,
		};

		try {
			setLoading(true);
			setSkeletonLoading(true);
			const response = await getPatientInvoicesAPI(body);
			if (response?.data?.data) {
				const invoices = response.data.data || [];
				const totalCount = response?.data?.total || response?.data?.count || 0;
				setListData(invoices);
				setTotalItems(Math.ceil(totalCount / size) || 0);
			} else {
				setListData([]);
				setTotalItems(0);
			}
		} catch (error) {
			console.error("Error fetching patient invoices:", error);
			setListData([]);
			setTotalItems(0);
		} finally {
			setLoading(false);
			setSkeletonLoading(false);
		}
	}, [
		activeTab,
		appliedFilters.patientId,
		appliedFilters.fromDate,
		appliedFilters.toDate,
		appliedFilters.invoiceType,
		appliedFilters.search,
		page,
		pharmacyPOS?.hospital_id,
		setLoading,
		size,
	]);

	useEffect(() => {
		getPermission();
	}, [getPermission]);

	useEffect(() => {
		const { patientId, patientName, activeTab: navTab } = location.state || {};
		if (!patientId || !patientName || patientId === appliedFilters.patientId) return;

		const tab = navTab === "paid" ? "paid" : "partial_paid";
		setSelectedPatientOption({ value: patientId, label: patientName });
		setSelectPatient(patientId);
		setActiveTab(tab);
		sessionStorage.setItem(TAB_STORAGE_KEY, tab);
		setAppliedFilters({
			patientId,
			fromDate: "",
			toDate: "",
			invoiceType: "all",
			search: "",
		});
		setPage(1);
		history(`${process.env.PUBLIC_URL}/patient-accounts`, { replace: true });
	}, [appliedFilters.patientId, history, location.state]);

	useEffect(() => {
		if (!navPatientState.hasNavPatient) return;
		sessionStorage.setItem(TAB_STORAGE_KEY, navPatientState.activeTab);
	}, [navPatientState.activeTab, navPatientState.hasNavPatient]);

	useEffect(() => {
		if (!pharmacyPOS?.hospital_id) return;
		getList();
	}, [getList, pharmacyPOS?.hospital_id]);

	const loadPatientOptions = useCallback(
		async (inputValue) => {
			if (!inputValue || inputValue.trim().length < 3) return [];

			try {
				const payload = {
					search: inputValue.trim(),
				};

				if (pharmacyPOS?.hospital_id) {
					payload.clinic_id = pharmacyPOS.hospital_id;
				}

				const response = await getAllPatients(payload);
				const patients = response.data?.data || [];
				return mapPatientsToOptions(patients, true, "hospital_patient");
			} catch (error) {
				console.error("Error loading patients:", error);
				return [];
			}
		},
		[pharmacyPOS?.hospital_id]
	);

	const handleFromDateChange = (event) => {
		const value = event.target.value;
		setFromDate(value);
		if (toDate && value && toDate < value) {
			setToDate("");
		}
	};

	const handleToDateChange = (event) => {
		setToDate(event.target.value);
	};

	const handleSearch = () => {
		setAppliedFilters({
			patientId: selectPatient,
			fromDate,
			toDate,
			invoiceType: selectType,
			search: searchValue.trim(),
		});
		setPage(1);
	};

	const handleClear = () => {
		setSelectPatient("");
		setSelectedPatientOption(null);
		setFromDate("");
		setToDate("");
		setSelectType("all");
		setSearchValue("");
		setAppliedFilters({
			patientId: "",
			fromDate: "",
			toDate: "",
			invoiceType: "all",
			search: "",
		});
		setPage(1);
	};

	const handleTabChange = (tab) => {
		if (tab === activeTab) return;
		sessionStorage.setItem(TAB_STORAGE_KEY, tab);
		setActiveTab(tab);
		setPage(1);
	};

	const handlePageChange = (pageNumber) => setPage(pageNumber);

	const handlePageSizeChange = (e) => {
		setSize(parseInt(e.target.value, 10));
		setPage(1);
	};

	const handlePreviewClick = (item) => {
		setPreviewInvoice(item);
		setPreviewModal(true);
	};

	const handleViewClick = (item) => {
		const invoiceId = item?.appointment_invoice?._id || item?.invoiceId || item?._id;
		savePatientAccountRecord(invoiceId, item);
		history(`${process.env.PUBLIC_URL}/patient-account-details/${invoiceId}`, {
			state: { record: item },
		});
	};

	const handleCollectClick = (item) => {
		setSelectedInvoice(item);
		setCollectModal(true);
	};

	const handleDispenseClick = (item) => {
		history(`${process.env.PUBLIC_URL}/new-sales`, {
			state: { prescriptionData: item },
		});
	};

	const handleCollectSubmit = async (payload) => {
		const appointmentId =
			payload.appointmentId || getCollectPaymentAppointmentId(selectedInvoice);

		if (!appointmentId) {
			toast.error(t("Appointment not found for this invoice."));
			return;
		}

		try {
			setIsSubmittingPayment(true);
			setLoading(true);
			const invoiceRecord = payload.invoiceRecord || selectedInvoice;
			const body = buildPayRemainingSalesPayload({
				appointmentId,
				paidAmount: payload.paidAmount,
				paymentType: payload.paymentMode,
				currencyType: payload.currency,
				isSettled: payload.markAsPaid,
				isConversion: payload.isConversion ?? payload.currency !== payload.baseCurrency,
				invoiceRecord,
				billing: payload.billing,
				patientId: payload.patientId || getPatientAccountPatientId(invoiceRecord),
				patientName:
					payload.patientName || getPatientDisplayName(invoiceRecord?.patient),
			});
			const response = await payRemainingSalesAPI(body);
			if (response?.data?.status === "success") {
				toast.success(response.data.message || t("Payment collected successfully."));
				setCollectModal(false);
				setSelectedInvoice(null);
				getList();
			} else {
				console.log(response?.data?.message);
				// toast.error(response?.data?.message || t("Unable to collect payment."));
			}
		} catch (error) {
			console.error("Error collecting payment:", error);
			// toast.error(
			// 	error?.response?.data?.message || t("Unable to collect payment.")
			// );
		} finally {
			setIsSubmittingPayment(false);
			setLoading(false);
		}
	};

	const getPatientLabel = (item) => getPatientDisplayName(item?.patient);

	const getFileNumber = (item) =>
		item?.patient?.health_id ||
		item?.file_no ||
		item?.file_number ||
		item?.patient?.file_no ||
		"-";
	const getInvoiceType = (isTreatmentPackage) => isTreatmentPackage ? t("Treatment Package") : t("Consultation");

	const getInvoiceRecord = (item) => normalizePatientAccountItem(item);

	const getInvoiceUrl = (item) =>
		item?.appointment_invoice?.invoice ||
		item?.invoice ||
		item?.invoice_url ||
		item?.invoiceUrl ||
		"";

	const listTitle =
		activeTab === "paid"
			? t("List Of Paid Invoices")
			: t("List Of Unpaid & Partial Paid Invoices");

	const renderActions = (item) => {
		const actions = [];
		const appointmentBillStatus = item?.appointment_invoice?.status;
		if (activeTab === "paid") {
			if (viewView) {
				actions.push(
					<button
						key="preview"
						type="button"
						className="success-action-button me-2"
						id={`preview-${item?._id}`}
						onClick={() => handlePreviewClick(item)}
						aria-label={t("Preview Invoice")}
					>
						<i className="fa fa-file-text-o" style={{ fontSize: "18px" }} />
						<ToolTip
							attrToolTip={{
								placement: "top",
								isOpen: actionTooltips[`preview-${item?._id}`] || false,
								target: `preview-${item?._id}`,
								toggle: () => toggleActionTooltip(`preview-${item?._id}`),
							}}
						>
							<strong>{t("Preview Invoice")}</strong>
						</ToolTip>
					</button>
				);
				actions.push(
					<button
						key="view"
						type="button"
						className="success-action-button"
						id={`view-${item?._id}`}
						onClick={() => handleViewClick(item)}
						aria-label={t("View")}
					>
						<i className="fa fa-eye" style={{ fontSize: "18px" }} />
						<ToolTip
							attrToolTip={{
								placement: "top",
								isOpen: actionTooltips[`view-${item?._id}`] || false,
								target: `view-${item?._id}`,
								toggle: () => toggleActionTooltip(`view-${item?._id}`),
							}}
						>
							<strong>{t("View")}</strong>
						</ToolTip>
					</button>
				);
			}
		} else if (viewView || collectView) {
			const hasPrescriptions = Array.isArray(item?.prescriptions) && item.prescriptions.length > 0;
			
			// if (hasPrescriptions) {
				actions.push(
					<button
						key="dispense"
						type="button"
						className="success-action-button"
						id={`dispense-${item?._id}`}
						onClick={() => handleDispenseClick(item)}
						aria-label={t("Dispense")}
					>
						<i className="fa fa-plus" style={{ fontSize: "18px" }} />
						<ToolTip
							attrToolTip={{
								placement: "top",
								isOpen: actionTooltips[`dispense-${item?._id}`] || false,
								target: `dispense-${item?._id}`,
								toggle: () => toggleActionTooltip(`dispense-${item?._id}`),
							}}
						>
							<strong>{t("Dispense")}</strong>
						</ToolTip>
					</button>
				);
			// }

			if (collectView) {
				actions.push(
					<button
						key="collect"
						type="button"
						className="success-action-button"
						id={`collect-${item?._id}`}
						onClick={() => handleCollectClick(item)}
						aria-label={t("Collect Payment")}
					>
						<i className="fa fa-money" style={{ fontSize: "18px" }} />
						<ToolTip
							attrToolTip={{
								placement: "top",
								isOpen: actionTooltips[`collect-${item?._id}`] || false,
								target: `collect-${item?._id}`,
								toggle: () => toggleActionTooltip(`collect-${item?._id}`),
							}}
						>
							<strong>{t("Collect Payment")}</strong>
						</ToolTip>
					</button>
				);
			}
			if (viewView) {
				if (appointmentBillStatus !== "unpaid") {
					actions.push(
						<button
							key="preview"
							type="button"
							className="success-action-button me-2"
							id={`preview-${item?._id}`}
							onClick={() => handlePreviewClick(item)}
							aria-label={t("Preview Invoice")}
						>
							<i className="fa fa-file-text-o" style={{ fontSize: "18px" }} />
							<ToolTip
								attrToolTip={{
									placement: "top",
									isOpen: actionTooltips[`preview-${item?._id}`] || false,
									target: `preview-${item?._id}`,
									toggle: () => toggleActionTooltip(`preview-${item?._id}`),
								}}
							>
								<strong>{t("Preview Invoice")}</strong>
							</ToolTip>
						</button>
					);
				}
				actions.push(
					<button
						key="view"
						type="button"
						className="success-action-button me-2"
						id={`view-${item?._id}`}
						onClick={() => handleViewClick(item)}
						aria-label={t("View Details")}
					>
						<i className="fa fa-eye" style={{ fontSize: "18px" }} />
						<ToolTip
							attrToolTip={{
								placement: "top",
								isOpen: actionTooltips[`view-${item?._id}`] || false,
								target: `view-${item?._id}`,
								toggle: () => toggleActionTooltip(`view-${item?._id}`),
							}}
						>
							<strong>{t("View Details")}</strong>
						</ToolTip>
					</button>
				);
			}
		}

		return (
			<div className="patient-accounts-actions d-flex justify-content-center align-items-center gap-2 flex-nowrap">
				{actions}
			</div>
		);
	};

	if (!canShowPatientAccounts(pharmacyPOS)) {
		return (
			<>
				<Breadcrumbs mainTitle={t("Patient Accounts")} parent={t("Dashboard")} title={t("Patient Accounts")} />
				<Alert color="warning" className="mx-3">
					{t(
						"Patient Accounts is available only when your pharmacy is connected to a hospital and is enabled to collect payments."
					)}
				</Alert>
			</>
		);
	}

	if (!listView && privileges?.permissions) {
		return (
			<>
				<Breadcrumbs mainTitle={t("Patient Accounts")} parent={t("Dashboard")} title={t("Patient Accounts")} />
				<div className="card" style={{ borderRadius: "15px" }}>
					<div className="card-body text-center py-5">{t("You do not have permission to view this module.")}</div>
				</div>
			</>
		);
	}

	return (
		<>
			<Breadcrumbs mainTitle={t("Patient Accounts")} parent={t("Dashboard")} title={t("Patient Accounts")} />
			<div className="row">
				<div className="col-sm-12">
					<div className="card patient-accounts-card" style={{ borderRadius: "15px" }}>
						<div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
							<H5 className="mb-0">{t("Patient Accounts")}</H5>
						</div>

						<div className="card-body" id="consultationCardStyle">
							<Nav tabs className="patient-accounts-tabs mb-3">
								<NavItem>
									<NavLink
										className={activeTab === "partial_paid" ? "active" : ""}
										onClick={() => handleTabChange("partial_paid")}
										href="#"
										role="tab"
										aria-selected={activeTab === "partial_paid"}
									>
										{t("Unpaid & Partial Paid")}
									</NavLink>
								</NavItem>
								<NavItem>
									<NavLink
										className={activeTab === "paid" ? "active" : ""}
										onClick={() => handleTabChange("paid")}
										href="#"
										role="tab"
										aria-selected={activeTab === "paid"}
									>
										{t("Paid Invoices")}
									</NavLink>
								</NavItem>
							</Nav>

							<TabContent activeTab={activeTab}>
								<TabPane tabId={activeTab}>
									<div className="filter-card border rounded p-3 mb-3">
										<h6 className="patient-accounts-list-title">{listTitle}</h6>
										<div className="row mb-3" style={{ alignItems: "center" }}>
											<Col sm="4" lg="4" xl="4" md="4">
												<label className="form-label" htmlFor="patient-select">
													{t("Patient")}
												</label>
												<AsyncSelect
													inputId="patient-select"
													classNamePrefix="react-select"
													cacheOptions
													isClearable
													loadOptions={loadPatientOptions}
													value={selectedPatientOption}
													onChange={(selectedOption) => {
														setSelectedPatientOption(selectedOption);
														setSelectPatient(selectedOption ? selectedOption.value : "");
													}}
													placeholder={t("Search patient by name or phone...")}
													noOptionsMessage={({ inputValue }) =>
														!inputValue || inputValue.length < 3
															? t("Type at least 3 characters")
															: t("No patients found")
													}
													menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
													menuShouldScrollIntoView
												/>
											</Col>
											<Col sm="3" lg="3" xl="3" md="3">
												<label className="form-label" htmlFor="from-date">
													{t("From Date")}
												</label>
												<Input
													id="from-date"
													type="date"
													className="formControlColor"
													style={{ lineHeight: 2, fontSize: "1rem !important" }}
													value={fromDate}
													onChange={handleFromDateChange}
													max={toDate || today}
													aria-label={t("From Date")}
												/>
											</Col>
											<Col sm="3" lg="3" xl="3" md="3">
												<label className="form-label" htmlFor="to-date">
													{t("To Date")}
												</label>
												<Input
													id="to-date"
													type="date"
													className="formControlColor"
													style={{ lineHeight: 2, fontSize: "1rem !important" }}
													value={toDate}
													onChange={handleToDateChange}
													min={fromDate || undefined}
													max={today}
													aria-label={t("To Date")}
												/>
											</Col>
											<Col sm="3" lg="2" xl="2" md="2">
												<label className="form-label" htmlFor="invoice-type">
													{t("Type")}
												</label>
												<Select
													inputId="invoice-type"
													classNamePrefix="react-select"
													options={typeOptions}
													value={typeOptions.find((option) => option.value === selectType) || typeOptions[0]}
													onChange={(selectedOption) =>
														setSelectType(selectedOption ? selectedOption.value : "")
													}
													placeholder={t("Select")}
													isClearable={false}
													components={{ Option: HighlightOptions }}
												/>
											</Col>
										</div>
										<div className="row my-3" style={{ alignItems: "center" }}>
											<Col sm="9" lg="9" xl="10" md="9">
												<label className="form-label" htmlFor="search-invoices">
													{t("Search")}
												</label>
												<Input
													id="search-invoices"
													type="text"
													placeholder={t("Search")}
													value={searchValue}
													onChange={(e) => setSearchValue(e.target.value)}
													className="formControlColor"
													style={{ lineHeight: 1.8, fontSize: "1rem !important" }}
													onKeyDown={(e) => {
														if (e.key === "Enter") handleSearch();
													}}
												/>
											</Col>
											<Col
												className="col-xl-2 col-lg-3 col-md-3 col-sm-3 d-flex justify-content-start align-items-center"
												style={{ gap: "18px", marginTop: "20px" }}
											>
												<button
													id="FilterToolTip"
													type="button"
													className="btn btn-primary"
													style={{ height: "40px" }}
													onClick={handleSearch}
												>
													<Filter />
												</button>
												<ToolTip
													attrToolTip={{
														placement: "top",
														isOpen: filterTooltipOpen,
														target: "FilterToolTip",
														toggle: () => setFilterTooltipOpen(!filterTooltipOpen),
													}}
												>
													<strong>{t("filter")}</strong>
												</ToolTip>
												<button
													id="ClearToolTip"
													type="button"
													className="btn btn-secondary"
													style={{ height: "40px" }}
													onClick={handleClear}
													aria-label={t("clear")}
												>
													<X />
												</button>
												<ToolTip
													attrToolTip={{
														placement: "top",
														isOpen: clearTooltipOpen,
														target: "ClearToolTip",
														toggle: () => setClearTooltipOpen(!clearTooltipOpen),
													}}
												>
													<strong>{t("clear")}</strong>
												</ToolTip>
											</Col>
										</div>
									</div>

									<div className="table-responsive rounded">
										<Table className="table-center table-bordered mb-0 table-hover">
											<thead>
												<tr>
													<th className="border-bottom p-3">#</th>
													<th className="border-bottom p-3">{t("Invoice No.")}</th>
													<th className="border-bottom p-3">{t("File No.")}</th>
													<th className="border-bottom p-3">{t("Type")}</th>
													<th className="border-bottom p-3">{t("Patient")}</th>
													<th className="border-bottom p-3">{t("Invoice Date")}</th>
													<th className="border-bottom p-3">{t("Invoice Status")}</th>
													{activeTab === "partial_paid" && (
														<>
															<th className="border-bottom p-3">{t("Invoice Amount")}</th>
															<th className="border-bottom p-3">{t("Paid Amount")}</th>
															<th className="border-bottom p-3">{t("Due Amount")}</th>
														</>
													)}
													{activeTab === "paid" && (
														<th className="border-bottom p-3">{t("Paid Amount")}</th>
													)}
													<th className="border-bottom p-3 text-center">{t("Actions")}</th>
												</tr>
											</thead>
											<tbody>
												{skeletonLoading ? (
													[...Array(3)].map((_, index) => (
														<tr key={index}>
															{[...Array(activeTab === "partial_paid" ? 11 : 9)].map((__, cellIndex) => (
																<td key={cellIndex}>
																	<Skeleton height={20} />
																</td>
															))}
														</tr>
													))
												) : listData.length > 0 ? (
													listData.map((item, index) => {
														const invoiceRecord = getInvoiceRecord(item);
														return (
															<tr key={item?._id || index}>
																<td>{(page - 1) * size + index + 1}</td>
																<td>{invoiceRecord.invoice_no || "-"}</td>
																<td>{getFileNumber(item)}</td>
																<td>{getInvoiceType(item.is_treatment_package) || "-"}</td>
																<td style={{ textTransform: "capitalize" }}>{getPatientLabel(item)}</td>
																<td>{formatDateList(invoiceRecord.invoice_date)}</td>
																<td>
																	<InvoiceStatusBadge
																		status={invoiceRecord.status}
																		isSettled={item?.appointment_invoice?.is_settled === true}
																	/>
																</td>
																{activeTab === "partial_paid" && (
																	<>
																		<td>
																			{formatAmount(
																				invoiceRecord.invoice_amount,
																				invoiceRecord.currency,
																				getCurrencySymbol
																			)}
																		</td>
																		<td>
																			<PatientAccountPaidAmount
																				amount={invoiceRecord.paid_amount}
																				currency={invoiceRecord.currency}
																				foreignPayments={invoiceRecord.foreign_payments}
																				getCurrencySymbol={getCurrencySymbol}
																			/>
																		</td>
																		<td>
																			{formatAmount(
																				invoiceRecord.due_amount,
																				invoiceRecord.currency,
																				getCurrencySymbol
																			)}
																		</td>
																	</>
																)}
																{activeTab === "paid" && (
																	<td>
																		<PatientAccountPaidAmount
																			amount={invoiceRecord.paid_amount}
																			currency={invoiceRecord.currency}
																			foreignPayments={invoiceRecord.foreign_payments}
																			getCurrencySymbol={getCurrencySymbol}
																		/>
																	</td>
																)}
																<td className="text-center">{renderActions(item)}</td>
															</tr>
														);
													})
												) : (
													<tr className="text-center">
														<td colSpan={activeTab === "partial_paid" ? 12 : 10}>
															{t("No invoice data found.")}
														</td>
													</tr>
												)}
											</tbody>
										</Table>
									</div>

									<Col sm="12" className="mt-4">
										<div className="d-md-flex align-items-center text-center justify-content-between">
											<div className="show-entries pt-3">
												<FormLabel>{t("Show")}&nbsp;</FormLabel>
												<select
													style={{
														display: "unset",
														width: "auto",
														padding: "0.375rem 2.25rem 0.375rem 0.75rem",
													}}
													value={size}
													onChange={handlePageSizeChange}
													className="form-control"
													aria-label={t("Entries per page")}
												>
													<option value={5}>5</option>
													<option value={10}>10</option>
													<option value={20}>20</option>
													<option value={30}>30</option>
													<option value={50}>50</option>
												</select>
												&nbsp;
												<FormLabel>{t("entries")}</FormLabel>
											</div>
											<MyPagination
												totalPages={totalItems}
												currentPage={page}
												onPageChange={handlePageChange}
											/>
										</div>
									</Col>
								</TabPane>
							</TabContent>
						</div>
					</div>
				</div>
			</div>

			<InvoicePreviewModal
				isOpen={previewModal}
				toggle={() => {
					setPreviewModal(false);
					setPreviewInvoice(null);
				}}
				invoiceUrl={getInvoiceUrl(previewInvoice)}
				invoiceNo={
					previewInvoice?.appointment_invoice?.invoice_number ||
					previewInvoice?.invoice_no ||
					previewInvoice?.invoice_number
				}
			/>

			<CollectPaymentModal
				isOpen={collectModal}
				toggle={() => {
					setCollectModal(false);
					setSelectedInvoice(null);
				}}
				invoice={selectedInvoice}
				onSubmit={handleCollectSubmit}
				isSubmitting={isSubmittingPayment}
			/>
		</>
	);
};

export default PatientAccounts;
